import React from 'react';
import { useDocumentRouteQuery } from '../hooks/useLegalRouteQueries';
import { useSuperAdminStore } from '../store/useSuperAdminStore';
import type {
  AdminDocumentPackageDetail,
  AdminLegalProfileUpdate,
  LegalPackageCommand,
} from '../legalTypes';
import { AdminButton } from './components';
import DocumentFilters from './legal/DocumentFilters';
import DocumentPackageDetail from './legal/DocumentPackageDetail';
import DocumentPackageTable from './legal/DocumentPackageTable';
import LegalPagination from './legal/LegalPagination';
import './styles/adminLegal.css';

const useDocumentsStore = () => ({
  packages: useSuperAdminStore((state) => state.documentPackages),
  page: useSuperAdminStore((state) => state.documentPackagePage),
  detail: useSuperAdminStore((state) => state.documentPackage),
  bundle: useSuperAdminStore((state) => state.documentBundle),
  profile: useSuperAdminStore((state) => state.legalProfile),
  conflict: useSuperAdminStore((state) => state.documentConflict),
  profileConflict: useSuperAdminStore((state) => state.legalProfileConflict),
  listLoading: useSuperAdminStore((state) => state.documentListLoading),
  detailLoading: useSuperAdminStore((state) => state.documentDetailLoading),
  mutating: useSuperAdminStore((state) => state.documentMutationLoading),
  error: useSuperAdminStore((state) => state.documentError),
  partialError: useSuperAdminStore((state) => state.documentPartialError),
  fetch: useSuperAdminStore((state) => state.fetchDocumentPackages),
  resolve: useSuperAdminStore((state) => state.resolveDocumentPackageCompany),
  load: useSuperAdminStore((state) => state.loadDocumentWorkspace),
  clear: useSuperAdminStore((state) => state.clearDocumentWorkspace),
  saveProfile: useSuperAdminStore((state) => state.saveLegalProfile),
  refreshProfile: useSuperAdminStore((state) => state.refreshLegalProfileConflict),
  runCommand: useSuperAdminStore((state) => state.executeDocumentCommand),
  refreshConflict: useSuperAdminStore((state) => state.refreshDocumentConflict),
  configureSigners: useSuperAdminStore((state) => state.configureDocumentSigners),
  runSignerCommand: useSuperAdminStore((state) => state.executeDocumentSignerCommand),
  completeSigner: useSuperAdminStore((state) => state.completeDocumentSigner),
  downloadDocument: useSuperAdminStore((state) => state.downloadDocumentArtifact),
  downloadPackage: useSuperAdminStore((state) => state.downloadDocumentPackage),
  uploadEvidence: useSuperAdminStore((state) => state.uploadDocumentEvidence),
});

const DocumentsEmpty = () => (
  <div role="status" aria-label="No document packages" className="admin-legal-empty">
    <strong>No document packages match these filters.</strong>
    <span>Clear filters or create a package from a verified company profile.</span>
  </div>
);

const DocumentsError: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div role="alert" aria-label="Documents unavailable" className="admin-legal-callout admin-legal-callout--danger">
    <strong>Legal administration is temporarily unavailable.</strong>
    <p>{message}</p>
    <AdminButton onClick={onRetry}>Retry documents</AdminButton>
  </div>
);

const AdminDocumentsRoute: React.FC = () => {
  const query = useDocumentRouteQuery();
  const store = useDocumentsStore();
  const [selectionResolutionError, setSelectionResolutionError] = React.useState<string | null>(null);
  const resolutionPackageId = React.useRef<string | null>(null);
  const fetchDocuments = store.fetch;
  const loadDocument = store.load;
  const clearDocument = store.clear;
  const resolveDocumentCompany = store.resolve;
  const setSelectedPackage = query.setSelectedPackage;
  const selectedPackageId = query.selectedPackageId;
  const documentListLoading = store.listLoading;
  const documentPage = store.page;
  const selectedRow = store.packages.find((item) => item.id === selectedPackageId);
  const selectedCompanyId = query.query.companyId ?? selectedRow?.companyId ?? null;
  React.useEffect(() => {
    void fetchDocuments(query.query).catch(() => undefined);
  }, [fetchDocuments, query.query]);
  React.useEffect(() => {
    const packageId = selectedPackageId;
    if (!packageId || selectedCompanyId) {
      resolutionPackageId.current = null;
      setSelectionResolutionError(null);
      return;
    }
    if (documentListLoading || !documentPage || resolutionPackageId.current === packageId) {
      return;
    }
    resolutionPackageId.current = packageId;
    setSelectionResolutionError(null);
    void resolveDocumentCompany(packageId).then((companyId) => {
      if (resolutionPackageId.current !== packageId) {
        return;
      }
      if (companyId) {
        setSelectedPackage(packageId, companyId);
        return;
      }
      setSelectionResolutionError(
        'The selected package could not be resolved. Return to the package results and open it again.'
      );
    }).catch(() => undefined);
  }, [
    selectedPackageId,
    setSelectedPackage,
    selectedCompanyId,
    documentListLoading,
    documentPage,
    resolveDocumentCompany,
  ]);
  React.useEffect(() => {
    if (query.selectedPackageId && selectedCompanyId) {
      void loadDocument(selectedCompanyId, query.selectedPackageId).catch(() => undefined);
    } else if (!query.selectedPackageId) {
      clearDocument();
    }
  }, [clearDocument, loadDocument, query.selectedPackageId, selectedCompanyId]);

  const closeDetail = () => {
    query.setSelectedPackage(null);
    store.clear();
  };
  const saveProfile = (input: AdminLegalProfileUpdate) => {
    if (!selectedCompanyId) {
      return Promise.reject(new Error('The selected package has no company.'));
    }
    return store.saveProfile(selectedCompanyId, input);
  };
  const downloadDocument = store.downloadDocument;
  const downloadPackage = store.downloadPackage;
  const runCommand = async (command: LegalPackageCommand): Promise<AdminDocumentPackageDetail> =>
    store.runCommand(command);
  const showListError = store.error && store.packages.length === 0 && !store.detailLoading;

  return (
    <section className="admin-legal-workspace" aria-labelledby="documents-title">
      <header className="admin-legal-page-header"><div><span className="admin-legal-eyebrow">Customer legal operations</span><h2 id="documents-title">Documents</h2><p>Search packages, review immutable versions, and run only server-authorized lifecycle actions.</p></div></header>
      <DocumentFilters search={query.searchText} purpose={query.purpose} jurisdiction={query.jurisdiction} assignee={query.assignee} state={query.state} updatedFrom={query.updatedFrom} signatureStatus={query.signatureStatus} missingInput={query.missingInput} generationFailure={query.generationFailure} archived={query.archived} onSearch={query.setSearchText} onFilter={query.setFilter} />
      {store.listLoading && <div role="status" aria-label="Loading documents" className="admin-legal-loading">Loading document packages…</div>}
      {showListError && <DocumentsError message={store.error?.message ?? 'Request failed.'} onRetry={() => void store.fetch(query.query).catch(() => undefined)} />}
      {!store.listLoading && !showListError && <DocumentPackageTable packages={store.packages} onOpen={(item) => query.setSelectedPackage(item.id, item.companyId)} emptyState={<DocumentsEmpty />} />}
      {!store.listLoading && !showListError && store.page && <LegalPagination label="Documents" page={store.page.page} pageSize={store.page.pageSize} totalCount={store.page.totalCount} onPage={query.setPage} />}
      {query.selectedPackageId && store.detailLoading && <div role="status" aria-label="Loading selected document package" className="admin-legal-loading">Loading package evidence…</div>}
      {selectionResolutionError && <div role="alert" aria-label="Selected package unavailable" className="admin-legal-callout admin-legal-callout--warning"><strong>Selected package unavailable.</strong><p>{selectionResolutionError}</p></div>}
      {store.detail && store.bundle && store.profile && <DocumentPackageDetail detail={store.detail} bundle={store.bundle} profile={store.profile} conflict={store.conflict} profileConflict={store.profileConflict} evidence={store.bundle.evidence} error={store.error} partialError={store.partialError} busy={store.mutating} onSaveProfile={saveProfile} onRefreshProfile={store.refreshProfile} onPackageCommand={runCommand} onRefreshConflict={store.refreshConflict} onConfigureSigners={store.configureSigners} onSignerCommand={store.runSignerCommand} onUpload={store.uploadEvidence} onComplete={store.completeSigner} onDownloadDocument={downloadDocument} onDownloadPackage={downloadPackage} onClose={closeDetail} />}
    </section>
  );
};

export default AdminDocumentsRoute;
